import React, {useCallback, useContext, useEffect, useState} from 'react';
import * as PropTypes from 'prop-types';
import {ErrorBoundary} from "react-error-boundary";
import ClientError from "../../sections/client_error/ClientError";
import {
  getDriverRouteById,
  getDriverRouteObjectFromPath,
} from "./driverRoutes";
import {Navigate, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
  encryptTextValue,
  getSessionData,
  getTimeFromMilliseconds,
} from "../../utility/global-util";
import {getSessionCookie, getSessionExpiryTimeCookie} from "../../utility/cookie-utility";
import {useTimer} from "react-use-precision-timer";
import {App, Button, Col, Modal, Row, Typography} from "antd";
import BannerPanel from "../panels/BannerPanel";
import {useTranslation} from "react-i18next";
import {asPortalUrl} from "../../utility/fetch-util";
import {checkDriverSession} from "../../api/security";
import GlobalContext from "../../GlobalContext";
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";

const { Text } = Typography;

const SESSION_COUNTDOWN_MINUTES = 2;  //time given to extend session before expiry

const SecureRoute = ({ children }) => {

  const { t } = useTranslation();

  const { isOnline } = useOnlineStatus();

  const globalData = useContext(GlobalContext);

  const navigate = useNavigate();

  const location = useLocation();
  const [searchParams, ] = useSearchParams();

  const { notification } = App.useApp();

  const [isSessionModalVisible, setIsSessionModalVisible] = useState(false);
  const [remainingSessionTime, setRemainingSessionTime] = useState(Number.MAX_VALUE);

  // This page is in the secure route - must be logged-in - check if current cookie matches last login
  const loggedInHash = getSessionData('loginHash');
  const compareToHash = encryptTextValue(getSessionCookie());
  const loc = useLocation();
  const isLoginPage = loc && loc.pathname === '/';
  const redirectToLogin = (loggedInHash !== compareToHash && !isLoginPage); //navigate to login if not login (stop loop) - this page is secure

  // Function to check if session expired
  const checkForSessionExpiry = useCallback(() => {
    const nowTimestamp = new Date().getTime();
    const expiryCookie = getSessionExpiryTimeCookie();
    const expiryTimestamp = expiryCookie != null ? Number(expiryCookie.toString()) : 0;
    const remainingTime = expiryTimestamp - nowTimestamp;
    if (remainingTime < (SESSION_COUNTDOWN_MINUTES * 60 * 1000) && isOnline) {
      setRemainingSessionTime(remainingTime);
      setIsSessionModalVisible(true);
    }
  }, [isOnline]);

  // Timer set to run every 1 second (countdown or check session every 5 seconds)
  const checkSessionCallback = React.useCallback(() => {
    checkForSessionExpiry();
  }, [checkForSessionExpiry]);
  const checkSessionTimer = useTimer({ delay: 1000 }, checkSessionCallback);
  useEffect(() => {
    checkSessionTimer.start();
  }, [checkSessionTimer]);

  // Action Session Expiry
  const actionSessionExpiry = (expired) => {
    if (expired) {
      // Expired session - redirect to login
      setIsSessionModalVisible(false);
      window.location.replace(asPortalUrl('/#/'));
    }
    else {
      // Extend session
      checkDriverSession()
          .then(res => {
            notification.destroy();
            setIsSessionModalVisible(false);
          })
          .catch(ignoreErr => {
            // error extending session - treat as expired
            setIsSessionModalVisible(false);
            window.location.replace(asPortalUrl('/#/'));
          });
    }
  }

  // Set tab bar based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const currentRoute = getDriverRouteObjectFromPath(currentPath);
    if (currentRoute) {
      globalData.setCurrentRouteId(currentRoute.id ? currentRoute.id : null);    //update this section on public and secure routes
      globalData.setCurrentPath(currentRoute.path ? currentRoute.path: null);
      globalData.setVehicleHeader(currentRoute.vehicleHeader ? currentRoute.vehicleHeader : null);
      globalData.setVehicleHeaderTrans(currentRoute.vehicleHeaderTrans ? currentRoute.vehicleHeaderTrans : null);
      globalData.setCurrentTabBarId(currentRoute.tabBarId ? currentRoute.tabBarId : null);
      globalData.setCurrentBackRouteId(currentRoute.backRouteId ? currentRoute.backRouteId : null);
      globalData.setCurrentBackRouteTrans(currentRoute.backRouteTrans ? currentRoute.backRouteTrans : null);
      globalData.setRequestedRoute(currentRoute.id ? currentRoute.id : null);
      globalData.setContractNo(searchParams && searchParams.get("contractNo") ? searchParams.get("contractNo") : null);
      console.log('navigated -> ' + currentPath + ' (vehicleHeader: ' + globalData.vehicleHeader + ', tabBarId: ' + globalData.currentTabBarId + ', backRouteId: ' + globalData.currentBackRouteId + ')');
    } else {
      console.log('navigated -> ' + currentPath);
    }
  }, [location.pathname, globalData, searchParams]);

  // Redirect to login or Show page with error boundary
  return (
      redirectToLogin
      ? <Navigate to={getDriverRouteById('start')}/>
      : <div>
          <ErrorBoundary FallbackComponent={ClientError} onReset={() => navigate(getDriverRouteById('home'))}>
            {children}
          </ErrorBoundary>
          <Modal
              className="modal_nopad_content"
              style={{ padding: '0' }}
              open={isSessionModalVisible}
              closable={false}
              maskClosable={false}
              onCancel={() => setIsSessionModalVisible(false)}
              keyboard={false}
              footer={null}>
            <BannerPanel title={t('title.session_expiry')} style={{ width: '100%' }}>
              <Row align="middle" justify="space-between" style={{ width: '100%' }}>
                <Col>
                  <Text>{remainingSessionTime < 5000 ? t('message.session_expired') : t('message.session_expires_in', {0: getTimeFromMilliseconds(remainingSessionTime)})}</Text>
                </Col>
                <Col>
                  <Button type="primary"  style={{float: 'right' }} onClick={() => actionSessionExpiry(remainingSessionTime < 5000)}>
                    {remainingSessionTime < 5000 ? t('action.ok') : t('action.extend')}
                  </Button>
                </Col>
              </Row>
            </BannerPanel>
          </Modal>
        </div>
  );
}

// Validate prop type parameters.
SecureRoute.propTypes = {
  children: PropTypes.element.isRequired,
};

export default SecureRoute;
